'use client';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocale } from 'use-intl';
import { useMedia } from '@ssv-website/hooks';
import styles from './SectionYearSelector.module.css';

interface SectionYearSelectorProps {
  selectedYear: number;
  years: number[];
  onChange?: (year: number) => void;
}

export default function SectionYearSelector({
  selectedYear,
  years,
  onChange,
}: SectionYearSelectorProps) {
  const locale = useLocale();
  const isMobile = useMedia('mobile');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showYearSheet, setShowYearSheet] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);

  const handleYearChange = (year: number) => {
    setShowDropdown(false);
    setShowYearSheet(false);
    onChange?.(year);
  };

  const handleYearButtonClick = () => {
    if (isMobile) {
      setShowYearSheet(true);
      return;
    }
    setShowDropdown((prev) => !prev);
  };

  useEffect(() => {
    if (!showYearSheet) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showYearSheet]);

  useEffect(() => {
    if (isMobile || !showDropdown) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target && selectorRef.current?.contains(target)) {
        return;
      }
      setShowDropdown(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isMobile, showDropdown]);

  const handleSelectorBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (isMobile || !showDropdown) {
      return;
    }

    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && selectorRef.current?.contains(nextTarget)) {
      return;
    }
    setShowDropdown(false);
  };

  return (
    <div
      ref={selectorRef}
      className={styles.yearSelector}
      onBlur={handleSelectorBlur}
    >
      <button
        onClick={handleYearButtonClick}
        className={styles.yearButton}
        aria-haspopup="listbox"
        aria-expanded={isMobile ? showYearSheet : showDropdown}
        type="button"
      >
        {selectedYear}
        <svg
          className={`${styles.yearIcon} ${
            showDropdown || showYearSheet ? styles.yearIconOpen : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {showDropdown ? (
        <div className={styles.dropdown}>
          {years.map((year) => (
            <button
              key={year}
              onClick={() => handleYearChange(year)}
              className={`${styles.dropdownItem} ${
                year === selectedYear ? styles.dropdownItemActive : ''
              }`}
              type="button"
            >
              {year}
            </button>
          ))}
        </div>
      ) : null}

      {isMobile &&
      showYearSheet &&
      typeof document !== 'undefined' &&
      createPortal(
        <div
          className={styles.sheetMask}
          onClick={() => setShowYearSheet(false)}
        >
          <div
            className={styles.sheet}
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.sheetTitle}>
              {locale === 'zh' ? '选择年份' : 'Select Year'}
              <button
                type="button"
                className={styles.sheetCloseButton}
                aria-label={locale === 'zh' ? '关闭' : 'Close'}
                onClick={() => setShowYearSheet(false)}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.94387 16.3758C4.91132 16.4084 4.85856 16.4084 4.82602 16.3758L3.62393 15.1738C3.59139 15.1412 3.59139 15.0885 3.62393 15.0559L8.67969 10.0002L3.62387 4.94434C3.59132 4.9118 3.59132 4.85903 3.62387 4.82649L4.82595 3.62441C4.85849 3.59187 4.91126 3.59187 4.9438 3.62441L9.99962 8.68023L15.0555 3.62436C15.088 3.59181 15.1408 3.59181 15.1733 3.62436L16.3754 4.82644C16.408 4.85898 16.408 4.91175 16.3754 4.94429L11.3196 10.0002L16.3754 15.056C16.4079 15.0885 16.4079 15.1413 16.3754 15.1738L15.1733 16.3759C15.1407 16.4084 15.088 16.4084 15.0554 16.3759L9.99962 11.3201L4.94387 16.3758Z"
                    fill="#959FAA"
                  />
                </svg>
              </button>
            </div>
            <div className={styles.sheetList}>
              {years.map((year) => {
                const isActive = year === selectedYear;
                return (
                  <button
                    key={year}
                    onClick={() => handleYearChange(year)}
                    className={`${styles.sheetItem} ${
                      isActive ? styles.sheetItemActive : ''
                    }`}
                    type="button"
                  >
                    <span className={styles.sheetItemLabel}>{year}</span>
                    {isActive ? (
                      <svg
                        className={styles.sheetItemIcon}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M5 12l5 5L20 7" />
                      </svg>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
